import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Award,
  Search,
  Download,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificatesPage = () => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, { withCredentials: true });
      setCertificates(response.data);
    } catch (error) {
      console.error("Error fetching certificates:", error);
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    const expDate = new Date(expiresAt);
    return expDate <= thirtyDays && expDate > new Date();
  };

  const getStatusBadge = (cert) => {
    if (isExpired(cert.expires_at)) {
      return <Badge className="badge-danger">Expired</Badge>;
    }
    if (isExpiringSoon(cert.expires_at)) {
      return <Badge className="badge-warning">Expiring Soon</Badge>;
    }
    return <Badge className="badge-success">Valid</Badge>;
  };

  const filteredCertificates = certificates.filter(cert =>
    cert.course_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.certificate_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownload = async (cert) => {
    try {
      // Download PDF from backend
      const response = await axios.get(`${API}/certificates/${cert.certificate_id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Determine file type from response
      const contentType = response.headers['content-type'];
      const isPdf = contentType?.includes('pdf');
      const extension = isPdf ? 'pdf' : 'txt';
      
      // Create download link
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${cert.certificate_number}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download certificate");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Stats
  const validCerts = certificates.filter(c => !isExpired(c.expires_at) && !isExpiringSoon(c.expires_at)).length;
  const expiringSoon = certificates.filter(c => isExpiringSoon(c.expires_at)).length;
  const expired = certificates.filter(c => isExpired(c.expires_at)).length;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="certificates-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Certificates
          </h1>
          <p className="text-muted-foreground mt-1">
            View and download training completion certificates.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valid</p>
                <p className="font-heading text-2xl font-bold text-primary">{validCerts}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="font-heading text-2xl font-bold text-amber-600">{expiringSoon}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="font-heading text-2xl font-bold text-rose-600">{expired}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search certificates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-certificates"
          />
        </div>

        {/* Certificates Grid */}
        {filteredCertificates.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCertificates.map((cert) => (
              <Card key={cert.certificate_id} className="card-hover overflow-hidden" data-testid={`cert-${cert.certificate_id}`}>
                {/* Certificate Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 text-white">
                  <div className="flex items-center justify-between">
                    <Award className="w-8 h-8 text-amber-400" />
                    {getStatusBadge(cert)}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{cert.certificate_number}</p>
                </div>
                
                <CardContent className="p-4 space-y-4">
                  <div>
                    <h3 className="font-heading font-semibold text-primary line-clamp-1">
                      {cert.course_title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{cert.employee_name}</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>Issued: {new Date(cert.issued_at).toLocaleDateString()}</span>
                    </div>
                    <Badge variant="secondary">{cert.score}%</Badge>
                  </div>
                  
                  {cert.expires_at && (
                    <div className="text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Expires: {new Date(cert.expires_at).toLocaleDateString()}
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => handleDownload(cert)}
                    data-testid={`download-${cert.certificate_id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Certificate
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Award className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                {searchQuery ? 'No Certificates Found' : 'No Certificates Yet'}
              </h3>
              <p className="text-muted-foreground">
                {searchQuery 
                  ? 'Try adjusting your search'
                  : 'Complete training courses to earn certificates'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CertificatesPage;
